import { corelCommunication } from "./communication";
import { terminalLog, sendToTerminal } from "../terminalLogger";
import { checkToken } from "../auth";
import { api } from "../api";
import { ensureSessionHelloOnce } from "../sessionHandshake";
import { API_URL, FRONTEND_URL } from "../../config";
import { getTenantIdForWebSocket } from "../../utils/getTenantId";
import { getShortSsoToken, secondsToExpiry } from "../toolsSso";
import { TokenService } from "../tokenService";
import { mapPermissionIdToRole } from "../permissionMapper";

export type OpenToolsResult = { ok: true } | { ok: false; error?: string };

// ============================================================================
// PlotCalc API Config - Constantes
// TODO: No futuro, buscar esses valores via GET na API (ex: /plotcalc/api-config)
// ============================================================================
// ⚠️ IMPORTANTE: tenantId aqui é um PARÂMETRO DA ROTA para buscar na tabela do PlotCalc
// NÃO tem relação com o tenant multi-tenant (X-Tenant-Id header)
// Este é um parâmetro específico da rota /plotcalc/config-full?tenantId=1
export function getPlotCalcTenantId(): number {
	// Retorna 1 como parâmetro da rota (não relacionado ao tenant multi-tenant)
	return 1;
}

const PLOTCALC_API_CONFIG = {
	baseUrl: API_URL, // http://localhost:3333 (sem barra no final)
	endpoint: "/plotcalc/config-full",
	get tenantId() {
		return getPlotCalcTenantId();
	},
} as const;

// isUnknownAction removida: fluxo agora é fire-and-forget

function getSessionTokenFromWindow(): string | null {
	try {
		return (
			(window as unknown as { sessionToken?: string }).sessionToken ?? null
		);
	} catch {
		return null;
	}
}

export async function ensureSessionToken(
	timeoutMs: number = 3000,
): Promise<boolean> {
	if (getSessionTokenFromWindow()) return true;
	return await ensureSessionHelloOnce(timeoutMs);
}

// ============================================================================
// PlotCalc API Config - Ensure (Pipeline para MontaTec)
// ============================================================================

export interface EnsurePlotCalcApiConfigOptions {
	/** Número máximo de tentativas (padrão: 3) */
	maxRetries?: number;
	/** Timeout por tentativa em ms (padrão: 5000) */
	timeoutPerRetry?: number;
	/** Delay entre tentativas em ms (padrão: 500) */
	retryDelay?: number;
}

export interface EnsurePlotCalcApiConfigResult {
	success: boolean;
	error?: string;
	attempts: number;
}

/**
 * Garante que a configuração da API do PlotCalc está salva na Registry.
 * Executa `plotcalc:set-api-config` com retry em caso de falha.
 *
 * Use esta função ANTES de executar cmd_montatec (command:1) ou cmd_mult_montatec (command:2)
 * para garantir que o VBA terá acesso à configuração correta.
 *
 * @param options Opções de retry
 * @returns Resultado com success, error e número de tentativas
 */
export async function ensurePlotCalcApiConfig(
	options: EnsurePlotCalcApiConfigOptions = {},
): Promise<EnsurePlotCalcApiConfigResult> {
	const { maxRetries = 3, timeoutPerRetry = 5000, retryDelay = 500 } = options;

	let attempts = 0;
	let lastError: string | undefined;

	const shortToken = getShortSsoToken() || TokenService.getAccessToken();
	const tenantId = PLOTCALC_API_CONFIG.tenantId;
	const multiTenantId = getTenantIdForWebSocket();

	const apiConfigData = {
		accessToken: shortToken,
		baseUrl: PLOTCALC_API_CONFIG.baseUrl,
		endpoint: PLOTCALC_API_CONFIG.endpoint,
		tenantId,
		multiTenantId,
	};

	sendToTerminal("🔧 [PLOTCALC_ENSURE] Iniciando garantia de config", {
		maxRetries,
		timeoutPerRetry,
		fullUrl: `${apiConfigData.baseUrl}${apiConfigData.endpoint}?tenantId=${tenantId}`,
	});

	while (attempts < maxRetries) {
		attempts++;

		try {
			const result = await new Promise<{ ok: boolean; error?: string }>(
				(resolve) => {
					const timeoutId = setTimeout(() => {
						resolve({ ok: false, error: `Timeout após ${timeoutPerRetry}ms` });
					}, timeoutPerRetry);

					const rid = corelCommunication.sendActionGuarded(
						"plotcalc:set-api-config",
						apiConfigData,
						(response: any) => {
							clearTimeout(timeoutId);

							if (response?.status === "error") {
								const msg = response?.message || "Erro desconhecido";
								resolve({ ok: false, error: msg });
							} else if (response?.status === "ok") {
								resolve({ ok: true });
							} else {
								// Resposta inesperada, considerar sucesso se não for explicitamente erro
								resolve({ ok: true });
							}
						},
					);

					if (!rid) {
						clearTimeout(timeoutId);
						resolve({ ok: false, error: "WebView indisponível" });
					}
				},
			);

			if (result.ok) {
				sendToTerminal("✅ [PLOTCALC_ENSURE] Config garantida com sucesso", {
					attempts,
					fullUrl: `${apiConfigData.baseUrl}${apiConfigData.endpoint}?tenantId=${tenantId}`,
				});
				return { success: true, attempts };
			}

			lastError = result.error;
			sendToTerminal(
				`⚠️ [PLOTCALC_ENSURE] Tentativa ${attempts}/${maxRetries} falhou`,
				{
					error: lastError,
				},
			);

			// Se não é a última tentativa, aguardar antes de retry
			if (attempts < maxRetries) {
				await new Promise((r) => setTimeout(r, retryDelay));
			}
		} catch (err) {
			lastError = err instanceof Error ? err.message : String(err);
			sendToTerminal(
				`❌ [PLOTCALC_ENSURE] Exceção na tentativa ${attempts}/${maxRetries}`,
				{
					error: lastError,
				},
			);

			if (attempts < maxRetries) {
				await new Promise((r) => setTimeout(r, retryDelay));
			}
		}
	}

	// Todas as tentativas falharam
	sendToTerminal("❌ [PLOTCALC_ENSURE] Todas as tentativas falharam", {
		attempts,
		lastError,
	});

	return {
		success: false,
		error:
			lastError || "Falha ao configurar PlotCalc após múltiplas tentativas",
		attempts,
	};
}

export async function openToolsDocker(options?: {
	toolId?: string;
}): Promise<OpenToolsResult> {
	try {
		const t0 = performance.now();
		terminalLog.info("menutools:click", { toolId: options?.toolId });

		// 1) Validar sessão com a API antes de abrir o Docker 2
		try {
			const isValid = await checkToken();
			if (!isValid) {
				sendToTerminal("❌ [OPEN_TOOLS] Sessão inválida", {
					reason: "auth_check_failed",
				});
				try {
					window.location.href = "/";
				} catch { }
				return { ok: false, error: "Sessão inválida. Faça login novamente." };
			}
		} catch (err) {
			sendToTerminal("❌ [OPEN_TOOLS] Erro ao validar sessão", {
				error: err instanceof Error ? err.message : String(err),
			});
			try {
				window.location.href = "/";
			} catch { }
			return { ok: false, error: "Falha ao validar sessão." };
		}
		sendToTerminal("⏱️ [OPEN_TOOLS] checkToken concluído", {
			elapsedMs: Math.round(performance.now() - t0),
		});

		// Preparar URL final com token curto no fragmento
		// ✅ USAR FRONTEND_URL centralizada ao invés de hardcoded localhost:3000
		const envToolsBase = (() => {
			try {
				return (import.meta as unknown as any)?.env?.VITE_TOOLS_BASE_URL as
					| string
					| undefined;
			} catch {
				return undefined;
			}
		})();
		const baseUrl = envToolsBase || FRONTEND_URL;

		// ✅ OTIMIZADO: Verificar se já existe token SSO válido antes de solicitar novo
		const existingSsoToken = getShortSsoToken();
		const secsToExpiry = secondsToExpiry();

		let shortToken: string;
		let userData: any = null;

		// Reutilizar token existente se ainda válido por mais de 5 minutos
		if (existingSsoToken && secsToExpiry && secsToExpiry > 300) {
			sendToTerminal("✅ [OPEN_TOOLS] Reutilizando token SSO existente", {
				secondsToExpiry: secsToExpiry,
			});
			shortToken = existingSsoToken;

			// ✅ CORRIGIDO: Obter dados do usuário do TokenService quando reutilizamos token
			const storedUserData = TokenService.getUserData();
			if (storedUserData?.data?.usuario) {
				userData = {
					user_id: storedUserData.data.usuario.id,
					user_nome: storedUserData.data.usuario.nome,
					user_login: storedUserData.data.usuario.login,
					user_isadmin: storedUserData.data.isAdmin || false,
					permissoes: storedUserData.data.permissions || [],
					permissionsConfig: storedUserData.data.permissionsConfig || [],
				};
				sendToTerminal("✅ [OPEN_TOOLS] Dados do usuário obtidos do storage", {
					hasUser: !!userData.user_id,
					hasPermissions: !!userData.permissoes?.length,
					hasConfig: !!userData.permissionsConfig?.length,
				});
			} else {
				sendToTerminal(
					"⚠️ [OPEN_TOOLS] Dados do usuário não encontrados no storage",
					{},
				);
			}
		} else {
			// Emitir token curto apenas se necessário
			const resp = await api("/auth/cdr/sso-token", { method: "POST" });
			shortToken = resp?.token;
			userData = resp?.user; // ✅ Usar dados da resposta da API

			if (!shortToken) {
				sendToTerminal("❌ [OPEN_TOOLS] Falha ao emitir token SSO", {
					response: resp,
				});
				return { ok: false, error: "Falha ao emitir token curto" };
			}
		}
		sendToTerminal("⏱️ [OPEN_TOOLS] SSO token pronto", {
			elapsedMs: Math.round(performance.now() - t0),
		});

		// Preparar userData mínimo no fragmento (base64url)
		const b64url = (s: string) =>
			btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
		let udParam = "";
		try {
			const u = userData || null;
			if (u) {
				const minimal = {
					usuario: { id: u.user_id, nome: u.user_nome, login: u.user_login },
					permissions: Array.isArray(u.permissoes)
						? u.permissoes.map(
							(p: {
								perm_id: number;
								name?: string;
								path?: string;
								perm_descricao?: string;
								perm_path?: string;
							}) => {
								const fallbackRole = mapPermissionIdToRole(p.perm_id);
								return {
									perm_id: p.perm_id,
									perm_descricao:
										p.perm_descricao ?? p.name ?? fallbackRole ?? '',
									perm_path:
										p.perm_path ?? p.path ??
										(fallbackRole ? '/' + fallbackRole : ''),
								};
							},
						)
						: [],
					permissionsConfig: Array.isArray(u.permissionsConfig)
						? u.permissionsConfig
						: [],
				};
				udParam = `&ud=${encodeURIComponent(b64url(JSON.stringify(minimal)))}`;
			}
		} catch (err) {
			terminalLog.warn("open-tools:userdata-failed", {
				error: err instanceof Error ? err.message : String(err),
			});
		}

		const toolPath = options?.toolId ? `/tools/${options.toolId}` : "/tools";
		const url = `${baseUrl}${toolPath}?mode=cdr#token=${encodeURIComponent(
			shortToken,
		)}${udParam}`;

		// 🔍 DEBUG: Log da URL construída
		sendToTerminal("🔗 [OPEN_TOOLS:URL_BUILT]", {
			baseUrl,
			toolPath,
			urlLength: url.length,
			hasToken: url.includes("#token="),
			urlPreview: url.substring(0, 100) + "...",
		});

		// Preparar bootstrap com dados completos para injeção direta pelo C#
		const bootstrap = {
			accessToken: shortToken,
			user: userData
				? {
					id: userData.user_id,
					nome: userData.user_nome,
					login: userData.user_login,
					isadmin: userData.user_isadmin,
				}
				: null,
			permissoes: userData?.permissoes || [],
			permissionsConfig: userData?.permissionsConfig || [],
			isAdmin: userData?.user_isadmin || false,
		};

		const payload: Record<string, any> = {
			url,
			bootstrap,
		};

		// Payload validado - silencioso

		// Fire-and-forget: não aguardamos resposta; apenas enviamos
		const requestId = corelCommunication.sendActionGuarded(
			"open-tools-docker",
			payload,
		);
		if (!requestId) {
			sendToTerminal(
				"❌ [OPEN_TOOLS] Falha ao enviar - WebView indisponível",
				{},
			);
			return { ok: false, error: "Bridge WebView indisponível." };
		}

		sendToTerminal("✅ [OPEN_TOOLS] Docker aberto", {
			requestId,
			elapsedMs: Math.round(performance.now() - t0),
		});
		terminalLog.info("open-tools-docker:sent");

		// ========================================================================
		// PlotCalc: Executar em PARALELO - set-api-config e apply-config
		// ========================================================================

		// 3) PlotCalc API Config: definir configuração da API na Registry do C#
		// ⚠️ tenantId aqui é parâmetro da rota (não relacionado ao tenant multi-tenant)
		const setApiConfigPromise = new Promise<{ ok: boolean; error?: string }>(
			(resolve) => {
				try {
					const tenantId = PLOTCALC_API_CONFIG.tenantId;
					const multiTenantId = getTenantIdForWebSocket(); // ✅ STRING: tenant multi-tenant para header X-Tenant-Id
					const apiConfigData = {
						accessToken: shortToken, // JWT token do login
						baseUrl: PLOTCALC_API_CONFIG.baseUrl,
						endpoint: PLOTCALC_API_CONFIG.endpoint,
						tenantId: tenantId, // ⚠️ NUMBER: parâmetro da rota para buscar na tabela (ex: 1)
						multiTenantId: multiTenantId, // ✅ STRING: tenant multi-tenant para header X-Tenant-Id (ex: 'one1printdf', 'dev')
					};

					sendToTerminal("🔧 [PLOTCALC_API_CONFIG] Enviando configuração", {
						fullUrl: `${apiConfigData.baseUrl}${apiConfigData.endpoint}?tenantId=${apiConfigData.tenantId}`,
						tenantId: tenantId,
						multiTenantId: multiTenantId,
					});

					const rid = corelCommunication.sendActionGuarded(
						"plotcalc:set-api-config",
						apiConfigData,
						(response: any) => {
							if (response?.status === "error") {
								const msg = response?.message || "Erro desconhecido";
								sendToTerminal("❌ [PLOTCALC_API_CONFIG] Erro", {
									message: msg,
								});
								terminalLog.warn("plotcalc:set-api-config:error", {
									message: msg,
								});
								resolve({ ok: false, error: msg });
							} else {
								sendToTerminal("✅ [PLOTCALC_API_CONFIG] Configuração salva", {
									fullUrl: response?.result?.config?.fullUrl,
									registryKey: response?.result?.registryKey,
								});
								resolve({ ok: true });
							}
						},
					);

					if (!rid) {
						sendToTerminal("❌ [PLOTCALC_API_CONFIG] Falha ao enviar", {});
						terminalLog.warn("plotcalc:set-api-config:send_failed");
						resolve({
							ok: false,
							error: "Falha ao enviar plotcalc:set-api-config",
						});
					}
				} catch (e) {
					const errorMsg = e instanceof Error ? e.message : String(e);
					sendToTerminal("❌ [PLOTCALC_API_CONFIG] Exceção", {
						error: errorMsg,
					});
					terminalLog.warn("plotcalc:set-api-config:exception", {
						error: errorMsg,
					});
					resolve({ ok: false, error: errorMsg });
				}
			},
		);

		// TODO: Temporariamente desabilitado - plotcalc:apply-config
		// 4) PlotCalc Apply Config: buscar config e enviar action dedicada ao C#
		// const applyConfigPromise = (async (): Promise<{ ok: boolean; error?: string }> => {
		//   try {
		//     // ✅ CORRIGIDO: Usar tenantId STRING (ex: 'one1printdf', 'dev', etc)
		//     const tenantId = getTenantIdForWebSocket();
		//     const cfg = await api(`/plotcalc/config?tenantId=${tenantId}`, { method: 'GET' });

		//     if (cfg && cfg.action === 'plotcalc:apply-config' && cfg.data) {
		//       const plotResp = await new Promise<any>((resolve) => {
		//         const outboundData = {
		//           forceReset: cfg?.data?.forceReset === true,
		//           serial: cfg?.data?.serial,
		//           profiles: Array.isArray(cfg?.data?.profiles) ? cfg.data.profiles : undefined
		//         } as { forceReset: boolean; serial?: string; profiles?: any[] };

		//         // Validação local mínima: evitar enviar payload vazio
		//         const hasAnyField = (typeof outboundData.serial === 'string' && outboundData.serial.length > 0)
		//           || (Array.isArray(outboundData.profiles) && outboundData.profiles.length > 0)
		//           || outboundData.forceReset === true;
		//
		//         if (!hasAnyField) {
		//           sendToTerminal('❌ [PLOTCALC] Payload vazio', {});
		//           resolve({ status: 'error', message: 'Nenhum campo enviado (validação local)' });
		//           return;
		//         }
		//
		//         const rid = corelCommunication.sendActionGuarded(
		//           'plotcalc:apply-config',
		//           outboundData,
		//           (response: any) => {
		//             if (response?.status === 'error') {
		//               sendToTerminal('❌ [PLOTCALC] Erro', { message: response?.message });
		//             }
		//             resolve(response);
		//           }
		//         );
		//         if (!rid) {
		//           sendToTerminal('❌ [PLOTCALC] Falha ao enviar', {});
		//           resolve(null);
		//         }
		//       });

		//       // Tratar falhas
		//       if (!plotResp) {
		//         terminalLog.error('plotcalc:apply-config:no_response');
		//         return { ok: false, error: 'Falha ao aplicar configuração do PlotCalc (sem resposta do host).' };
		//       }
		//       const respStatus = String(plotResp?.status || '').toLowerCase();
		//       if (respStatus !== 'ok') {
		//         const msg = plotResp?.message || plotResp?.result?.mensagem || 'Falha ao aplicar configuração do PlotCalc';
		//         sendToTerminal('❌ [PLOTCALC] Status não-ok', { message: msg });
		//         return { ok: false, error: String(msg) };
		//       }
		//       return { ok: true };
		//     } else {
		//       sendToTerminal('❌ [PLOTCALC] Configuração ausente', {});
		//       return { ok: false, error: 'Montatec não foi validado. Configuração PlotCalc ausente.' };
		//     }
		//   } catch (e) {
		//     sendToTerminal('❌ [PLOTCALC] Erro ao obter configuração', {
		//       error: e instanceof Error ? e.message : String(e)
		//     });
		//     return { ok: false, error: 'Montatec não foi validado. Falha ao obter configuração (GET).' };
		//   }
		// })();

		// Fire-and-forget: não bloquear abertura do Docker 2
		setApiConfigPromise.then((result) => {
			const elapsed = Math.round(performance.now() - t0);
			if (!result.ok) {
				sendToTerminal("⚠️ [PLOTCALC] set-api-config falhou (background)", {
					error: result.error,
					elapsedMs: elapsed,
				});
				terminalLog.warn("plotcalc:set-api-config:failed", {
					error: result.error,
					elapsedMs: elapsed,
				});
			} else {
				sendToTerminal("✅ [PLOTCALC] set-api-config OK (background)", {
					elapsedMs: elapsed,
				});
			}
		});

		const totalMs = Math.round(performance.now() - t0);
		sendToTerminal("⏱️ [OPEN_TOOLS] openToolsDocker concluído", { totalMs });
		return { ok: true };
	} catch (e: any) {
		sendToTerminal("❌ [OPEN_TOOLS] Erro inesperado", {
			error: e?.message || String(e),
			stack: e?.stack,
		});
		terminalLog.error("menutools:error", {
			errorMessage: e?.message || String(e),
		});
		return {
			ok: false,
			error: e?.message || "Erro inesperado ao abrir Ferramentas.",
		};
	}
}
